// Servicio para interactuar con la API de MiniMax (MiniMax-M2.5 / MiniMax-M3)
// Utiliza la especificación compatible con Anthropic Messages API

import { getAiConfig } from './aiConfigService';

/**
 * Realiza una llamada HTTP a la API de MiniMax (compatible con Anthropic Messages API)
 * @param {Object} params
 * @param {string} params.prompt - Mensaje del usuario
 * @param {string} [params.systemPrompt] - Prompt del sistema
 * @param {Object} [params.customConfig] - Configuración personalizada opcional
 * @returns {Promise<{ text: string, thinking: string, usage: { inputTokens: number, outputTokens: number }, rawResponse: Object }>}
 */
export const callMiniMaxApi = async ({ prompt, systemPrompt = 'You are a helpful assistant.', customConfig = null }) => {
  const config = customConfig || getAiConfig();
  const apiKey = config.minimaxApiKey?.trim();

  if (!apiKey) {
    throw new Error('No se ha configurado la API Key de MiniMax. Ingrésala en el módulo de Parámetros de IA.');
  }

  // Si se usa '/api/minimax', se aprovecha el proxy local de Vite hacia https://api.minimax.io/anthropic
  const baseUrl = (config.minimaxBaseUrl || '/api/minimax').replace(/\/+$/, '');
  let url;
  if (baseUrl.endsWith('/messages')) {
    url = baseUrl;
  } else if (baseUrl.endsWith('/v1')) {
    url = `${baseUrl}/messages`;
  } else {
    url = `${baseUrl}/v1/messages`;
  }

  const payload = {
    model: config.minimaxModel || 'MiniMax-M2.5',
    max_tokens: Number(config.maxTokens) || 4000,
    temperature: Number(config.temperature) || 0.2,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: prompt,
          },
        ],
      },
    ],
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMessage = `Error HTTP ${response.status}: ${response.statusText}`;
    try {
      const errText = await response.text();
      if (errText && (errText.trim().startsWith('<') || errText.includes('<html') || errText.includes('<!DOCTYPE'))) {
        errorMessage = `Respuesta HTML no esperada del servidor (${response.status} ${response.statusText}).`;
      } else {
        try {
          const errJson = JSON.parse(errText);
          errorMessage =
            errJson.error?.message ||
            errJson.msg ||
            errJson.message ||
            errJson.base_resp?.status_msg ||
            errText;
        } catch {
          errorMessage = errText || errorMessage;
        }
      }
    } catch {
      // Ignorar error al leer texto
    }

    if (response.status === 404) {
      throw new Error(
        '🌐 Error HTTP 404: La ruta del proxy (/api/minimax) no fue encontrada en producción. Despliega la nueva versión en Netlify para que se active la regla de redirección hacia api.minimax.io.'
      );
    }

    if (
      response.status === 429 ||
      errorMessage.includes('Token Plan usage limit') ||
      errorMessage.includes('buy credits') ||
      errorMessage.includes('2067')
    ) {
      throw new Error(
        '🚫 Límite de saldo/cuota alcanzado en tu cuenta de MiniMax (Error 2067). Para usar MiniMax necesitas recargar créditos o activar consumo en tu consola de MiniMax (platform.minimax.io). Si lo deseas, puedes alternar temporalmente a Gemini en "⚙️ Parámetros & Tokens IA".'
      );
    }

    if (
      response.status === 401 ||
      errorMessage.toLowerCase().includes('login fail') ||
      errorMessage.toLowerCase().includes('authentication_error')
    ) {
      throw new Error(
        '🔑 Error de autenticación en MiniMax: La API Key ingresada no es válida o no está autorizada. Por favor revisa la clave en "⚙️ Parámetros & Tokens IA".'
      );
    }

    if (response.status === 500 && (errorMessage.includes('Internal Server Error') || !errorMessage)) {
      throw new Error(
        '🌐 Error HTTP 500 (Proxy / Red): No se pudo establecer conexión con api.minimax.io a través del proxy. Verifica la conexión a internet.'
      );
    }

    throw new Error(`MiniMax API: ${errorMessage}`);
  }

  const result = await response.json();

  let thinking = '';
  let text = '';

  if (Array.isArray(result.content)) {
    for (const block of result.content) {
      if (block.type === 'thinking') {
        thinking += (block.thinking || '') + '\n';
      } else if (block.type === 'text') {
        text += (block.text || '');
      }
    }
  } else if (typeof result.content === 'string') {
    text = result.content;
  }

  const inputTokens = result.usage?.input_tokens || 0;
  const outputTokens = result.usage?.output_tokens || 0;

  return {
    text: text.trim(),
    thinking: thinking.trim(),
    usage: {
      inputTokens,
      outputTokens,
      totalTokens: inputTokens + outputTokens,
    },
    rawResponse: result,
  };
};

/**
 * Prueba rápida de conexión para el Playground de MiniMax
 */
export const testMiniMaxConnection = async (userMessage = 'Hi, how are you?', customConfig = null) => {
  const startTime = performance.now();
  const response = await callMiniMaxApi({
    prompt: userMessage,
    systemPrompt: 'You are a helpful assistant.',
    customConfig,
  });
  const durationMs = Math.round(performance.now() - startTime);

  return {
    ...response,
    durationMs,
  };
};

/**
 * Limpia y parsea un bloque de texto JSON retornado por el modelo
 */
const extractAndParseJson = (rawText) => {
  let jsonText = rawText.trim();
  // Remover markdown codeblocks
  jsonText = jsonText.replace(/```json\s*/i, '').replace(/```\s*$/i, '').trim();

  try {
    return JSON.parse(jsonText);
  } catch (error) {
    // Si hay texto antes o después del JSON, intentar extraer entre la primera '{' y la última '}'
    const firstBrace = jsonText.indexOf('{');
    const lastBrace = jsonText.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const slice = jsonText.substring(firstBrace, lastBrace + 1);
      return JSON.parse(slice);
    }
    throw new Error('La respuesta del modelo MiniMax no contiene un JSON válido: ' + error.message);
  }
};

/**
 * Valida y estructura los datos extraídos para el formulario de actas
 */
const validateAndCleanData = (data) => {
  return {
    tipoActa: data.tipoActa || '',
    fecha: data.fecha || '',
    horaInicio: data.horaInicio || '',
    horaFinal: data.horaFinal || '',
    lugar: data.lugar || '',
    objetivo: data.objetivo || '',
    ordenDia: Array.isArray(data.ordenDia)
      ? data.ordenDia.map((item) => ({
          titulo: item.titulo || '',
          descripcion: item.descripcion || '',
        }))
      : [],
    compromisos: Array.isArray(data.compromisos)
      ? data.compromisos.map((item) => ({
          compromiso: item.compromiso || '',
          responsable: item.responsable || '',
          fechaCumplimiento: item.fechaCumplimiento || '',
        }))
      : [],
    asistentes: Array.isArray(data.asistentes)
      ? data.asistentes.map((item) => ({
          nombre: item.nombre || '',
          cargo: item.cargo || '',
          asistio: item.asistio || 'Presencial',
          email: item.email || '',
        }))
      : [],
    responsable: data.responsable || '',
  };
};

/**
 * Analiza una transcripción usando MiniMax
 */
export const analyzeTranscriptionMiniMax = async (transcription, customConfig = null) => {
  const prompt = `
Analiza la siguiente transcripción de una reunión y extrae la información relevante para completar un acta de reunión.
Devuelve ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "tipoActa": "string - tipo de acta (ej: 'Reunión del contrato o convenio', 'Comité técnico', etc.)",
  "fecha": "string - fecha en formato YYYY-MM-DD si se menciona",
  "horaInicio": "string - hora de inicio en formato HH:MM si se menciona",
  "horaFinal": "string - hora de finalización en formato HH:MM si se menciona",
  "lugar": "string - lugar de la reunión (presencial, virtual, híbrido)",
  "objetivo": "string - objetivo principal de la reunión",
  "ordenDia": [
    {
      "titulo": "string - título del punto",
      "descripcion": "string - descripción del punto tratado"
    }
  ],
  "compromisos": [
    {
      "compromiso": "string - descripción del compromiso",
      "responsable": "string - persona responsable",
      "fechaCumplimiento": "string - fecha límite en formato YYYY-MM-DD si se menciona"
    }
  ],
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se menciona",
      "asistio": "string - 'Presencial', 'Virtual' o 'No asistió'"
    }
  ],
  "responsable": "string - persona que lidera o modera la reunión"
}

INSTRUCCIONES IMPORTANTES PARA ASISTENTES:
- Si en la transcripción aparecen nombres de personas con formato de timestamp y dos puntos (ej: "[10:01 a. m.] Laura Sánchez Ramírez:"), esto indica que esa persona está participando virtualmente.
- Si una persona habla pero su nombre no aparece explícitamente como etiqueta en el texto, asume que está presente físicamente.
- Para participantes virtuales, marca "asistio": "Virtual"
- Para participantes presenciales, marca "asistio": "Presencial"
- Si no puedes determinar el modo de participación, usa "Presencial" por defecto.

Si algún campo no se puede determinar de la transcripción, déjalo como string vacío "" o array vacío [].

Transcripción a analizar:
${transcription}
`;

  const apiRes = await callMiniMaxApi({
    prompt,
    systemPrompt: 'Eres un asistente experto en estructurar actas de reunión a partir de transcripciones. Responde siempre con un objeto JSON estricto.',
    customConfig,
  });

  const parsed = extractAndParseJson(apiRes.text);
  const cleanedData = validateAndCleanData(parsed);

  return {
    data: cleanedData,
    usage: apiRes.usage,
    thinking: apiRes.thinking,
  };
};

/**
 * Analiza transcripción junto con lista de asistencia CSV usando MiniMax
 */
export const analyzeTranscriptionWithAttendanceMiniMax = async (transcription, csvContent, customConfig = null) => {
  const prompt = `
Analiza la siguiente transcripción de una reunión junto con la lista de asistencia en formato CSV de Microsoft Teams y extrae la información relevante para completar un acta de reunión.

DEVUELVE ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "tipoActa": "string - tipo de acta (ej: 'Reunión del contrato o convenio', 'Comité técnico', etc.)",
  "fecha": "string - fecha en formato YYYY-MM-DD si se menciona",
  "horaInicio": "string - hora de inicio en formato HH:MM si se menciona",
  "horaFinal": "string - hora de finalización en formato HH:MM si se menciona",
  "lugar": "string - lugar de la reunión (presencial, virtual, híbrido)",
  "objetivo": "string - objetivo principal de la reunión",
  "ordenDia": [
    {
      "titulo": "string - título del punto",
      "descripcion": "string - descripción del punto tratado"
    }
  ],
  "compromisos": [
    {
      "compromiso": "string - descripción del compromiso",
      "responsable": "string - persona responsable",
      "fechaCumplimiento": "string - fecha límite en formato YYYY-MM-DD si se menciona"
    }
  ],
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se menciona",
      "asistio": "string - 'Presencial', 'Virtual' o 'No asistió'",
      "email": "string - correo electrónico del asistente si está disponible"
    }
  ],
  "responsable": "string - persona que lidera o modera la reunión"
}

INSTRUCCIONES IMPORTANTES PARA ASISTENTES:
- Usa PRIORITARIAMENTE la información del CSV de asistencia para determinar quién asistió a la reunión
- El CSV contiene información de participantes con sus correos electrónicos y duración de participación
- Si una persona aparece en el CSV con duración > 0, marca "asistio": "Virtual" (asumiendo que Teams es virtual)
- Relaciona los nombres de la transcripción con los del CSV usando coincidencias parciales de nombres
- Incluye el email del CSV cuando esté disponible

Transcripción a analizar:
${transcription}

Lista de asistencia CSV:
${csvContent}
`;

  const apiRes = await callMiniMaxApi({
    prompt,
    systemPrompt: 'Eres un asistente experto en estructurar actas de reunión y listas de asistencia. Responde únicamente con un objeto JSON estricto.',
    customConfig,
  });

  const parsed = extractAndParseJson(apiRes.text);
  const cleanedData = validateAndCleanData(parsed);

  return {
    data: cleanedData,
    usage: apiRes.usage,
    thinking: apiRes.thinking,
  };
};

/**
 * Analiza únicamente archivo CSV con MiniMax
 */
export const analyzeCsvOnlyMiniMax = async (csvContent, customConfig = null) => {
  const prompt = `
Analiza la siguiente lista de asistencia en formato CSV de Microsoft Teams y extrae únicamente la información de los asistentes.

DEVUELVE ÚNICAMENTE un objeto JSON válido con la siguiente estructura (sin texto adicional, solo el JSON):

{
  "asistentes": [
    {
      "nombre": "string - nombre del asistente",
      "cargo": "string - cargo o rol si se puede inferir del email o nombre",
      "asistio": "Virtual",
      "email": "string - correo electrónico del asistente si está disponible"
    }
  ]
}

INSTRUCCIONES IMPORTANTES:
- Extrae todos los participantes del CSV que tengan duración > 0
- Marca todos como "asistio": "Virtual" (asumiendo que Teams es virtual)
- Incluye el email cuando esté disponible

CSV a analizar:
${csvContent}
`;

  const apiRes = await callMiniMaxApi({
    prompt,
    systemPrompt: 'Eres un asistente experto en procesar archivos de asistencia CSV. Responde únicamente con un objeto JSON estricto.',
    customConfig,
  });

  const parsed = extractAndParseJson(apiRes.text);

  return {
    data: {
      asistentes: Array.isArray(parsed.asistentes)
        ? parsed.asistentes.map((a) => ({
            nombre: a.nombre || '',
            cargo: a.cargo || '',
            asistio: a.asistio || 'Virtual',
            email: a.email || '',
          }))
        : [],
    },
    usage: apiRes.usage,
    thinking: apiRes.thinking,
  };
};
